import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { business } from '../data/business';
import { LanguageToggle } from './LanguageToggle';
import { Logo } from './Logo';
export function Header() {
  const {
    t
  } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const links = [{
    href: '#services',
    label: t.nav.services
  }, {
    href: '#philosophy',
    label: t.nav.philosophy
  }, {
    href: '#about',
    label: t.nav.about
  }, {
    href: '#contact',
    label: t.nav.contact
  }];
  const solid = scrolled || open;
  return <header className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,color,box-shadow] duration-300 ease-in-out ${solid ? 'border-mist bg-white text-charcoal shadow-[0_6px_24px_-16px_rgba(17,24,39,0.25)]' : 'border-transparent bg-transparent text-white shadow-none'}`} data-id="element-150" data-mp="2hp4z8si0ezzz">
      <a href="#main" className="sr-only rounded-full bg-ink px-4 py-2 text-white focus:not-sr-only focus:absolute focus:left-4 focus:top-4" data-id="element-151" data-mp="30mpqu7c5scoa">
        {t.nav.skip}
      </a>
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-6 md:px-10" data-id="element-152" data-mp="3tbde28ggxc4h">
        <a href="#top" className="relative z-10 mr-4 shrink-0 cursor-pointer py-2" data-id="element-153" data-mp="1rdtcbws5s14w">
          <Logo light={!solid} data-id="element-154" data-mp="1wco6tvzhne4u" />
        </a>

        <nav aria-label="Primary" className="hidden lg:block" data-id="element-155" data-mp="1wbctb4g8qs0j">
          <ul className="flex items-center gap-9" data-id="element-156" data-mp="1ho1g6y4euzyb">
            {links.map(l => <li key={l.href} data-id="element-157" data-mp="1mmuxz2qqdd4a">
                <a href={l.href} className={`whitespace-nowrap text-[14px] font-medium tracking-[0.04em] transition-colors duration-200 ${solid ? 'hover:text-violet' : 'opacity-90 hover:opacity-100'}`} data-id="element-158" data-mp="1mm30fmdkp5bd">
                  {l.label}
                </a>
              </li>)}
          </ul>
        </nav>

        <div className="hidden items-center gap-6 lg:flex" data-id="element-159" data-mp="13p1unzc7316d">
          <a href={business.phoneHref} aria-label={`${t.nav.call}: ${business.phoneDisplay}`} className="flex items-center gap-2 whitespace-nowrap text-[13px] opacity-80 transition-opacity duration-200 hover:opacity-100" data-id="element-160" data-mp="2t2x65g8icnu6">
            <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-161" data-mp="mexvwsv5bmrn" />
            {business.phoneDisplay}
          </a>
          <LanguageToggle data-id="element-162" data-mp="hdqnqsp3clkg" />
          <a href="#contact" className="btn btn-sm btn-primary" data-id="element-163" data-mp="w4zmpsevvja9">
            {t.nav.book}
          </a>
        </div>

        <div className="flex items-center gap-1 lg:hidden" data-id="element-164" data-mp="rcg9iwk3yq5z">
          <LanguageToggle data-id="element-165" data-mp="154cu8fjg5xsc" />
          <button type="button" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? t.nav.closeMenu : t.nav.openMenu} onClick={() => setOpen(o => !o)} className="inline-flex h-12 w-12 items-center justify-center" data-id="element-166" data-mp="153l0zbdboc58">
            {open ? <XIcon className="h-6 w-6" data-id="element-167" data-mp="1a2egf9c66d81" /> : <MenuIcon className="h-6 w-6" data-id="element-168" data-mp="3gu6vyi71beid" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-169" data-mp="3bvvjdzpcupwg">
        {open && <motion.div id="mobile-menu" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="h-[calc(100dvh-5rem)] overflow-y-auto border-t border-mist bg-cream px-6 pb-10 pt-6 lg:hidden" data-id="element-170" data-mp="3bx6ny1qmk1om">
            <nav aria-label="Mobile" data-id="element-171" data-mp="2gxeyp68myd1d">
              <ul className="divide-y divide-mist" data-id="element-172" data-mp="2gwc8z1n1ugn1">
                {links.map(l => <li key={l.href} data-id="element-173" data-mp="1ka0kzgud8u71">
                    <a href={l.href} onClick={() => setOpen(false)} className="flex min-h-[64px] items-center font-display text-[22px] font-medium text-ink" data-id="element-174" data-mp="1p70qnqnazuxm">
                      {l.label}
                    </a>
                  </li>)}
              </ul>
            </nav>
            <div className="mt-10 flex flex-col gap-4" data-id="element-175" data-mp="11fljnkkl0pg1">
              <a href="#contact" onClick={() => setOpen(false)} className="btn btn-primary w-full" data-id="element-176" data-mp="15i8593qnte4s">
                {t.nav.book}
              </a>
              <a href={business.phoneHref} className="flex min-h-[56px] items-center justify-center gap-2 text-[15px] text-muted" data-id="element-177" data-mp="xxed0i57bkxp">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-178" data-mp="ox94u5s6oiih" />
                {business.phoneDisplay}
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}